/**
 * A service page's Managers section (#11635 slice 1, Round 8, R26; Round 9,
 * R28; Round 18, R67): one card per manager the service delivers documents
 * for, then the record linked at setup, in Home's Recent records grid and
 * shape: the manager glyph in the card's icon tile, the name, and "Manager ·
 * N documents" (the kind alone for a manager holding nothing).
 *
 * A card for a manager with a record opens it; a card with none is plain.
 * The grid is the same at every width, inset as Home's is.
 */
import React from "react";
import type { PortalService } from "@/components/FpFundBrain/Manage/Connections/InvestorPortalsRow";
import EntityIcon from "@/components/FpPages/RMS/Dashboard/Components/EntityIcon";
import { CARD_GRID, HomeCard, Section } from "./HomeSections";
import type { LinkedEntity } from "./browser/api";
import { serviceManagers } from "./browser/portals";
import { recordIconKind, recordMetaLabel } from "./browser/records";

export interface ServiceManagersProps {
  service: PortalService;
  /** Opening a manager's record, as a location. */
  onOpenRecord?: (record: LinkedEntity) => void;
}

const ServiceManagers: React.FC<ServiceManagersProps> = ({
  service,
  onOpenRecord,
}) => {
  const cards = serviceManagers(service);
  return (
    <Section title="Managers">
      {cards.length === 0 ? (
        <p className="m-0 px-4 text-[13px] text-[var(--fp-ink-3)]">
          No managers linked yet
        </p>
      ) : (
        <div className={CARD_GRID}>
          {cards.map((card) => {
            const publicId = card.publicId;
            return (
              <div key={publicId ?? card.name} data-testid="manager-card">
                <HomeCard
                  name={card.name}
                  meta={recordMetaLabel("manager", card.documentCount)}
                  glyph={
                    <EntityIcon
                      kind={recordIconKind("manager")}
                      size={18}
                      aria-hidden
                      className="text-[var(--fp-ink-2)]"
                    />
                  }
                  onOpen={
                    publicId !== null && onOpenRecord
                      ? () =>
                          onOpenRecord({
                            kind: card.kind,
                            publicId,
                            name: card.name,
                          })
                      : undefined
                  }
                />
              </div>
            );
          })}
        </div>
      )}
    </Section>
  );
};

export default ServiceManagers;
